/**
 * Per-browser cache for SuperAdmin diagnostic results (RLS scan, PHI
 * de-identification tests).
 *
 * These runs are slow and cost real API calls, so closing the modal or the tab
 * should not throw the results away. Everything cached here is either schema
 * metadata (table and policy names) or a synthetic patient — no real PHI.
 *
 * Every access is guarded: localStorage throws outright in some privacy modes
 * and when site data is blocked, and a diagnostics panel must not break for it.
 */
const PREFIX = "hipaa:v1:";

export const RLS_SCAN_KEY = "rls-scan";
export const PHI_DEID_TESTS_KEY = "phi-deid-tests";
export const MEMORY_ISSUES_KEY = "memory-issues-verify";
export const DB_HEALTH_KEY = "db-health-scan";
export const DB_HEALTH_LIVE_KEY = "db-health-live";
export const CODE_REVIEW_KEY = "code-review-run";
export const MEMORY_SCAN_KEY = "memory-scan";
export const AUDIT_LOGS_KEY = "audit-logs";
export const AUDIT_RETENTION_KEY = "audit-logs-retention";
export const IMPERSONATION_AUDIT_KEY = "impersonation-audit";
export const AUTH_HARDENING_KEY = "auth-hardening-status";
export const AUTH_HARDENING_USERS_KEY = "auth-hardening-users";
export const ACCESS_REVIEW_HISTORY_KEY = "access-review-history";

export function readCache(key) {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("data" in parsed)) return null;
    return parsed;                       // { data, savedAt }
  } catch {
    return null;
  }
}

export function writeCache(key, data) {
  try {
    window.localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ data, savedAt: new Date().toISOString() })
    );
    return true;
  } catch {
    // Quota exceeded or storage blocked — the panel still works, it just
    // won't remember across visits.
    return false;
  }
}

export function removeCache(key) {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* nothing to do */
  }
}

/** "just now" / "4 min ago" / "2 hr ago" / "3 days ago" */
export function formatAge(savedAt) {
  if (!savedAt) return "";
  const then = new Date(savedAt).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
